import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { DocumentoDescargaService } from './documento-descarga.service';
import { DocumentoVisorComponent } from './documento-visor.component';
import { tamanoLegible } from './documento.util';
import { ICONO_FORMATO } from './exportar-conversacion.component';
import { Exportacion, ExportarConversacionService } from './exportar-conversacion.service';

/** A partir de aquí se avisa de que una conversación larga tarda más (no es un cuelgue). */
const AVISO_LENTO_S = 15;

/**
 * Bandeja de «Exportar conversación»: una tarjeta por exportación pedida, arriba a la
 * derecha y por encima de cualquier pantalla (vive en el layout, no en el chat). Mientras
 * el servidor maqueta enseña una barra indeterminada y los segundos que lleva; al acabar,
 * «Vista previa» (visor en un diálogo) y «Descargar». Antes el resultado aparecía de golpe
 * al final del chat, fuera de la vista, sin ninguna señal de que se estuviera generando.
 *
 * Las tarjetas terminadas no se cierran solas: tienen acciones, y una notificación que
 * desaparece con un botón dentro no da tiempo a usarlo a quien lee despacio.
 */
@Component({
  selector: 'sg-exportar-bandeja',
  standalone: true,
  imports: [TranslatePipe, Dialog, DocumentoVisorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (svc.exportaciones().length) {
      <section class="bandeja" [attr.aria-label]="'agent.documentos.bandeja.region' | translate" data-testid="export-bandeja">
        @for (e of svc.exportaciones(); track e.clave) {
          <article class="tarjeta" [class]="'tarjeta--' + e.estado" data-testid="export-aviso" [attr.data-estado]="e.estado">
            <div class="tarjeta__icono" aria-hidden="true">
              <i class="pi" [class]="e.estado === 'error' ? 'pi-exclamation-triangle' : iconos[e.formato]"></i>
              @if (e.estado === 'listo') { <span class="tarjeta__check"><i class="pi pi-check"></i></span> }
            </div>

            <div class="tarjeta__cuerpo">
              <div class="tarjeta__cabecera">
                <span class="tarjeta__titulo" role="status">
                  @switch (e.estado) {
                    @case ('generando') { {{ 'agent.documentos.bandeja.generando' | translate: { formato: (nombreFormato(e) | translate) } }} }
                    @case ('listo') { {{ 'agent.documentos.bandeja.listo' | translate }} }
                    @default { {{ 'agent.documentos.bandeja.error' | translate }} }
                  }
                </span>
                @if (e.estado === 'generando' && segundos(e) >= 1) {
                  <span class="tarjeta__tiempo" data-testid="export-segundos">{{ segundos(e) }} s</span>
                }
                <button type="button" class="tarjeta__cerrar" (click)="svc.descartar(e.clave)" data-testid="export-cerrar"
                  [title]="(e.estado === 'generando' ? 'agent.documentos.bandeja.cancelar' : 'common.close') | translate"
                  [attr.aria-label]="(e.estado === 'generando' ? 'agent.documentos.bandeja.cancelar' : 'common.close') | translate">
                  <i class="pi pi-times"></i>
                </button>
              </div>

              <div class="tarjeta__sub" [title]="e.titulo">{{ e.titulo || ('agent.documentos.bandeja.sinTitulo' | translate) }}</div>

              @switch (e.estado) {
                @case ('generando') {
                  <div class="tarjeta__barra" role="progressbar" [attr.aria-label]="'agent.documentos.exportando' | translate"><span></span></div>
                  @if (segundos(e) >= avisoLento) {
                    <div class="tarjeta__nota">{{ 'agent.documentos.bandeja.paciencia' | translate }}</div>
                  }
                }
                @case ('listo') {
                  @if (e.doc; as d) {
                    <div class="tarjeta__meta">
                      <span class="tarjeta__formato">{{ d.format }}</span>
                      @if (d.pages) { <span>{{ 'agent.documentos.paginas' | translate: { n: d.pages } }}</span> }
                      @if (tamano(d.size)) { <span>{{ tamano(d.size) }}</span> }
                    </div>
                    <div class="tarjeta__acciones">
                      @if (d.preview) {
                        <button type="button" class="tarjeta__btn tarjeta__btn--principal" (click)="svc.visor.set(d)" data-testid="export-ver">
                          <i class="pi pi-eye"></i> {{ 'agent.documentos.vistaPrevia' | translate }}
                        </button>
                      }
                      <button type="button" class="tarjeta__btn" (click)="descargar(e)" [disabled]="descargando().has(e.clave)"
                        data-testid="export-descargar">
                        <i class="pi" [class]="descargando().has(e.clave) ? 'pi-spinner pi-spin' : 'pi-download'"></i>
                        {{ 'agent.documentos.descargar' | translate }}
                      </button>
                    </div>
                    @if (errorDescarga().has(e.clave)) {
                      <div class="tarjeta__error" role="alert">{{ 'agent.documentos.errorDescarga' | translate }}</div>
                    }
                  }
                }
                @default {
                  <div class="tarjeta__error" role="alert">{{ (e.errorKey || 'agent.documentos.errorGenerico') | translate }}</div>
                  <div class="tarjeta__acciones">
                    <button type="button" class="tarjeta__btn" (click)="svc.reintentar(e)" data-testid="export-reintentar">
                      <i class="pi pi-refresh"></i> {{ 'agent.documentos.reintentar' | translate }}
                    </button>
                  </div>
                }
              }
            </div>
          </article>
        }
      </section>
    }

    <!-- Diferido: el diálogo y el visor no viajan en el bundle inicial (la bandeja vive
         en el layout); se descargan la primera vez que alguien pide una vista previa.
         baseZIndex: en móvil la barra de navegación inferior va a z-index 10000 y tapaba
         el pie del visor (mismo arreglo que el hub de skills). -->
    @defer (when !!svc.visor()) {
      <p-dialog [header]="svc.visor()?.title || ''" [visible]="!!svc.visor()" (visibleChange)="!$event && svc.visor.set(null)"
        [modal]="true" [dismissableMask]="true" appendTo="body" [baseZIndex]="10000" [style]="{ width: 'min(900px, 95vw)' }">
        @if (svc.visor(); as dv) {
          <app-documento-visor [doc]="dv" [cerrable]="false"></app-documento-visor>
        }
      </p-dialog>
    }
  `,
  styles: [`
    .bandeja {
      position: fixed; top: 16px; right: 24px; z-index: 1000;
      display: flex; flex-direction: column; gap: 8px;
      width: 344px; max-width: calc(100vw - 32px);
      font-family: 'Urbanist', 'Inter', sans-serif;
    }
    @media (max-width: 600px) {
      .bandeja { right: 16px; left: 16px; width: auto; top: 12px; }
    }

    .tarjeta {
      display: flex; gap: 12px; padding: 12px 12px 12px 14px;
      background: #141416; border: 1px solid rgba(255, 255, 255, 0.14); border-radius: 12px;
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55);
      color: #ffffff;
      animation: tarjeta-entra 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    }
    @keyframes tarjeta-entra {
      from { opacity: 0; transform: translateX(12px); }
      to { opacity: 1; transform: none; }
    }

    .tarjeta__icono {
      position: relative; flex: none; display: inline-flex; align-items: center; justify-content: center;
      width: 36px; height: 36px; border-radius: 9px;
      background: #0e0e10; border: 1px solid rgba(255, 255, 255, 0.10);
      color: #A8C5F0; font-size: 15px;
    }
    .tarjeta--error .tarjeta__icono { color: #E28B4F; border-color: rgba(226, 139, 79, 0.35); }
    .tarjeta__check {
      position: absolute; right: -5px; bottom: -5px;
      display: inline-flex; align-items: center; justify-content: center;
      width: 16px; height: 16px; border-radius: 50%;
      background: #7FE3B0; color: #000000; border: 2px solid #141416;
    }
    .tarjeta__check .pi { font-size: 8px; font-weight: 700; }

    .tarjeta__cuerpo { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .tarjeta__cabecera { display: flex; align-items: center; gap: 8px; min-height: 22px; }
    .tarjeta__titulo { flex: 1; min-width: 0; font-size: 13.5px; font-weight: 600; }
    .tarjeta__tiempo {
      font-family: 'Space Mono', 'JetBrains Mono', 'SF Mono', monospace; font-size: 11px;
      color: rgba(255, 255, 255, 0.52); font-variant-numeric: tabular-nums;
    }
    .tarjeta__cerrar {
      flex: none; display: inline-flex; align-items: center; justify-content: center;
      width: 22px; height: 22px; margin: -2px -4px 0 0; padding: 0;
      background: transparent; border: none; border-radius: 6px;
      color: rgba(255, 255, 255, 0.52); cursor: pointer;
    }
    .tarjeta__cerrar .pi { font-size: 10px; }
    .tarjeta__cerrar:hover { background: #1f1f22; color: #ffffff; }
    .tarjeta__cerrar:focus-visible, .tarjeta__btn:focus-visible { outline: 2px solid #A8C5F0; outline-offset: 1px; }

    .tarjeta__sub {
      font-size: 12.5px; color: rgba(255, 255, 255, 0.72);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }

    /* Indeterminada: el servidor no informa de progreso, así que no se inventa un %. */
    .tarjeta__barra {
      position: relative; height: 3px; margin-top: 8px; border-radius: 999px; overflow: hidden;
      background: rgba(168, 197, 240, 0.12);
    }
    .tarjeta__barra span {
      position: absolute; top: 0; bottom: 0; left: 0; width: 40%; border-radius: inherit;
      background: #A8C5F0;
      animation: barra-avanza 1.3s cubic-bezier(0.65, 0, 0.35, 1) infinite;
    }
    @keyframes barra-avanza {
      from { transform: translateX(-100%); }
      to { transform: translateX(250%); }
    }
    .tarjeta__nota { margin-top: 6px; font-size: 12px; color: rgba(255, 255, 255, 0.52); }

    .tarjeta__meta {
      display: flex; gap: 10px; font-size: 12px; color: rgba(255, 255, 255, 0.52);
    }
    .tarjeta__formato { text-transform: uppercase; font-family: 'Space Mono', 'JetBrains Mono', 'SF Mono', monospace; }

    .tarjeta__acciones { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
    .tarjeta__btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 12px; border-radius: 999px; cursor: pointer;
      font: 600 12px 'Urbanist', 'Inter', sans-serif;
      background: transparent; color: #A8C5F0; border: 1px solid rgba(255, 255, 255, 0.14);
      transition: background 0.12s ease, border-color 0.12s ease;
    }
    .tarjeta__btn .pi { font-size: 11px; }
    .tarjeta__btn:hover:not(:disabled) { background: rgba(168, 197, 240, 0.1); border-color: rgba(168, 197, 240, 0.35); }
    .tarjeta__btn:disabled { opacity: 0.5; cursor: default; }
    .tarjeta__btn--principal { background: #A8C5F0; border-color: #A8C5F0; color: #000000; }
    .tarjeta__btn--principal:hover:not(:disabled) { background: #bcd3f4; border-color: #bcd3f4; }

    .tarjeta__error { margin-top: 4px; font-size: 12px; color: #E28B4F; }

    @media (prefers-reduced-motion: reduce) {
      .tarjeta { animation: none; }
      .tarjeta__barra span { animation-duration: 3s; }
    }
  `],
})
export class ExportarBandejaComponent {
  protected readonly svc = inject(ExportarConversacionService);
  private readonly descarga = inject(DocumentoDescargaService);

  protected readonly iconos = ICONO_FORMATO;
  protected readonly avisoLento = AVISO_LENTO_S;
  protected readonly descargando = signal<ReadonlySet<string>>(new Set());
  protected readonly errorDescarga = signal<ReadonlySet<string>>(new Set());

  /** Reloj de la bandeja: solo late mientras hay algo generándose. */
  private readonly ahora = signal(Date.now());
  private readonly hayEnCurso = computed(() => this.svc.exportaciones().some(e => e.estado === 'generando'));

  constructor() {
    let reloj: ReturnType<typeof setInterval> | null = null;
    const parar = () => { if (reloj) clearInterval(reloj); reloj = null; };
    effect(() => {
      if (!this.hayEnCurso()) { parar(); return; }
      if (reloj) return;
      this.ahora.set(Date.now());
      reloj = setInterval(() => this.ahora.set(Date.now()), 1000);
    });
    inject(DestroyRef).onDestroy(parar);
  }

  protected segundos(e: Exportacion): number {
    return Math.max(0, Math.floor((this.ahora() - e.inicio) / 1000));
  }

  protected nombreFormato(e: Exportacion): string {
    return `agent.documentos.formatoCorto.${e.formato}`;
  }

  protected tamano(bytes: number): string {
    return tamanoLegible(bytes);
  }

  protected async descargar(e: Exportacion): Promise<void> {
    if (!e.doc) return;
    this.descargando.update(s => new Set(s).add(e.clave));
    this.errorDescarga.update(s => { const n = new Set(s); n.delete(e.clave); return n; });
    try {
      await this.descarga.descargar(e.doc);
    } catch {
      this.errorDescarga.update(s => new Set(s).add(e.clave));
    } finally {
      this.descargando.update(s => { const n = new Set(s); n.delete(e.clave); return n; });
    }
  }
}
